import { useRef, useEffect, useCallback, useState } from 'react';

const THRESHOLD = 60;
const MAX_PULL = 100;

export function usePullToRefresh(onRefresh: () => Promise<unknown> | void) {
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const isPulling = useRef(false);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (window.scrollY > 5) return;
    startY.current = e.touches[0].clientY;
    isPulling.current = true;
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isPulling.current) return;
    const delta = e.touches[0].clientY - startY.current;
    if (delta < 0) {
      isPulling.current = false;
      setPullDistance(0);
      setPulling(false);
      return;
    }
    const dist = Math.min(delta * 0.5, MAX_PULL);
    setPullDistance(dist);
    setPulling(dist > 10);
    if (dist > 10) {
      e.preventDefault();
    }
  }, []);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling.current) return;
    isPulling.current = false;

    if (pullDistance >= THRESHOLD) {
      setRefreshing(true);
      setPullDistance(THRESHOLD);
      try {
        await onRefresh();
      } catch { /* swallow */ }
      setRefreshing(false);
    }
    setPullDistance(0);
    setPulling(false);
  }, [pullDistance, onRefresh]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd);
    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const progress = Math.min(pullDistance / THRESHOLD, 1);
  const showIndicator = pulling || refreshing;

  const PullIndicator = showIndicator ? (
    <div
      className="flex justify-center overflow-hidden transition-all duration-200"
      style={{ height: pulling || refreshing ? `${Math.max(pullDistance, refreshing ? 40 : 0)}px` : '0px' }}
    >
      <div className={`flex items-center gap-2 text-xs text-text-secondary ${refreshing ? 'animate-pulse' : ''}`}>
        <svg
          width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          className="transition-transform duration-200"
          style={{ transform: `rotate(${progress * 180}deg)` }}
        >
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
        <span>{refreshing ? 'Refreshing...' : progress >= 1 ? 'Release to refresh' : 'Pull to refresh'}</span>
      </div>
    </div>
  ) : null;

  return { containerRef, PullIndicator };
}
